import {useAtomEffect} from 'src/utils/jotai/useAtomEffect'

import {TITLE} from '@/constants/config'
import isInApp from '@/lib/in-app/utils/isInApp'
import {localeAtom} from '@/lib/locale/locale'
import {themeAtom} from '@/lib/theme/theme'
import useAbortControllerEffect from '@/utils/hooks/useAbortControllerEffect'
import postMessageToReactNative from '@/utils/postMessageToReactNative'

import setIsInApp from './utils/setIsInApp'
import shouldSetIsInApp from './utils/shouldSetIsInApp'

const SCROLL_THRESHOLD = 10

function sendCurrentTitle() {
  const currentTitle = document.title.replace(` - ${TITLE}`, '')

  postMessageToReactNative({
    type: 'title',
    title: currentTitle,
  })
}

export default memo(function InAppEffects() {
  useEffect(() => {
    if (shouldSetIsInApp()) {
      setIsInApp()
    }
  }, [])

  useAbortControllerEffect(function scrollTrackingForInApp(abortController) {
    if (!isInApp()) return

    document.documentElement.setAttribute('data-in-app', 'true')

    // Disable zoom via viewport meta tag

    let viewport: HTMLMetaElement | null = document.querySelector('meta[name="viewport"]')
    if (!viewport) {
      viewport = document.createElement('meta')
      viewport.name = 'viewport'
      document.head.appendChild(viewport)
    }
    viewport.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
    )

    // Disable zoom gestures
    document.addEventListener(
      'gesturestart',
      e => {
        e.preventDefault()
      },
      {signal: abortController.signal},
    )
    document.addEventListener(
      'gesturechange',
      e => {
        e.preventDefault()
      },
      {signal: abortController.signal},
    )
    document.addEventListener(
      'gestureend',
      e => {
        e.preventDefault()
      },
      {signal: abortController.signal},
    )

    // Disable double-tap zoom
    let lastTouchEnd = 0
    document.addEventListener(
      'touchend',
      event => {
        const now = new Date().getTime()
        if (now - lastTouchEnd <= 300) {
          event.preventDefault()
        }
        lastTouchEnd = now
      },
      {signal: abortController.signal},
    )

    // Scroll tracking functionality
    let lastScrollY = 0
    let ticking = false

    function updateScrollDirection() {
      const scrollY = window.scrollY || document.documentElement.scrollTop
      const scrollDirection = scrollY > lastScrollY ? 'down' : 'up'
      const scrollDelta = Math.abs(scrollY - lastScrollY)

      if (scrollDelta > SCROLL_THRESHOLD) {
        postMessageToReactNative({
          type: 'scroll',
          direction: scrollDirection,
          scrollY,
          lastScrollY,
        })
        lastScrollY = scrollY
      }
      ticking = false
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(updateScrollDirection)
        ticking = true
      }
    }

    window.addEventListener('scroll', onScroll, {passive: true, signal: abortController.signal})
  }, [])

  useEffect(function titleTrackingForInApp() {
    if (!isInApp()) return

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- cannot be null
    const target = document.querySelector('title')!
    const observer = new MutationObserver(mutations => {
      mutations.forEach(() => {
        sendCurrentTitle()
      })
    })
    const config = {
      childList: true,
    }
    observer.observe(target, config)

    return () => {
      observer.disconnect()
    }
  }, [])

  const canGoBack = useCanGoBack()

  useEffect(
    function canGoBackTrackingForInApp() {
      if (!isInApp()) return

      postMessageToReactNative({
        type: 'can-go-back',
        data: canGoBack,
      })
    },
    [canGoBack],
  )

  useAtomEffect(
    localeAtom,
    useCallback(locale => {
      if (!isInApp()) return

      postMessageToReactNative({
        type: 'locale',
        locale,
      })

      console.log(locale)
    }, []),
  )

  useAtomEffect(
    themeAtom,
    useCallback(theme => {
      if (!isInApp()) return

      postMessageToReactNative({
        type: 'theme',
        theme,
      })

      console.log(theme)
    }, []),
  )

  return null
})
